import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FlightTakeoff as TravelIcon,
} from "@mui/icons-material";
import useNotification from "../../hooks/common/useNotification";
import { travelRequestTypesAPI } from "../../services/api/travelRequestTypes.api";
import EnhancedDataTable from "../../components/features/finance/EnhancedDataTable";

const TravelRequestTypeSettingsPage = () => {
  const { showSuccess, showError } = useNotification();

  // State management
  const [travelRequestTypes, setTravelRequestTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    requires_advance: false,
    is_active: true,
    icon: "business",
    color: "primary",
    display_order: 0,
  });

  // Icon options
  const iconOptions = [
    { value: "person", label: "Person" },
    { value: "business", label: "Business" },
    { value: "school", label: "School/Training" },
    { value: "location_on", label: "Location" },
    { value: "emergency", label: "Emergency" },
    { value: "flight_takeoff", label: "Flight Takeoff" },
    { value: "public", label: "International/Public" },
    { value: "work", label: "Work" },
    { value: "event", label: "Event" },
  ];

  // Color options
  const colorOptions = [
    { value: "default", label: "Default" },
    { value: "primary", label: "Primary" },
    { value: "secondary", label: "Secondary" },
    { value: "success", label: "Success" },
    { value: "info", label: "Info" },
    { value: "warning", label: "Warning" },
    { value: "error", label: "Error" },
  ];

  // Table columns
  const columns = [
    {
      field: "display_order",
      headerName: "Order",
      width: 80,
      type: "number",
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      flex: 1,
    },
    {
      field: "code",
      headerName: "Code",
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.row.code}
          size="small"
          variant="outlined"
          sx={{ fontFamily: "monospace" }}
        />
      ),
    },
    {
      field: "description",
      headerName: "Description",
      width: 250,
      flex: 1,
    },
    {
      field: "requires_advance",
      headerName: "Requires Advance",
      width: 150,
      type: "boolean",
      renderCell: (params) => (
        <Chip
          label={params.row.requires_advance ? "Yes" : "No"}
          size="small"
          color={params.row.requires_advance ? "primary" : "default"}
        />
      ),
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? "Active" : "Inactive"}
          size="small"
          color={params.row.is_active ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchTravelRequestTypes();
  }, []);

  const fetchTravelRequestTypes = async () => {
    try {
      setLoading(true);
      const response = await travelRequestTypesAPI.getAll();
      setTravelRequestTypes(response.data.data.travelRequestTypes || []);
    } catch (error) {
      showError(error.message || "Failed to fetch travel request types");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingType(null);
    setFormData({
      name: "",
      code: "",
      description: "",
      requires_advance: false,
      is_active: true,
      icon: "business",
      color: "primary",
      display_order: travelRequestTypes.length + 1,
    });
    setDialogOpen(true);
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      code: type.code,
      description: type.description || "",
      requires_advance: type.requires_advance || false,
      is_active: type.is_active,
      icon: type.icon || "business",
      color: type.color || "primary",
      display_order: type.display_order || 0,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (type) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${type.name}"? This action cannot be undone.`
      )
    ) {
      try {
        await travelRequestTypesAPI.delete(type.id);
        showSuccess("Travel request type deleted successfully");
        fetchTravelRequestTypes();
      } catch (error) {
        showError(error.message || "Failed to delete travel request type");
      }
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingType) {
        await travelRequestTypesAPI.update(editingType.id, formData);
        showSuccess("Travel request type updated successfully");
      } else {
        await travelRequestTypesAPI.create(formData);
        showSuccess("Travel request type created successfully");
      }
      setDialogOpen(false);
      fetchTravelRequestTypes();
    } catch (error) {
      showError(error.message || "Failed to save travel request type");
    }
  };

  const handleToggleStatus = async (type) => {
    try {
      await travelRequestTypesAPI.toggleStatus(type.id);
      showSuccess(
        `Travel request type ${type.is_active ? "deactivated" : "activated"} successfully`
      );
      fetchTravelRequestTypes();
    } catch (error) {
      showError(error.message || "Failed to toggle status");
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <TravelIcon fontSize="large" />
            Travel Request Types
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Manage travel request types for your organization
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Travel Type
        </Button>
      </Box>

      <Card>
        <CardContent>
          <EnhancedDataTable
            data={travelRequestTypes}
            columns={columns}
            loading={loading}
            onRowDoubleClick={handleEdit}
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingType ? "Edit Travel Request Type" : "Add Travel Request Type"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                helperText="Display name for the travel type"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Code"
                value={formData.code}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    code: e.target.value.toLowerCase().replace(/\s+/g, "_"),
                  })
                }
                required
                helperText="Unique code (lowercase, underscores only)"
                inputProps={{ pattern: "[a-z_]+" }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                multiline
                rows={3}
                helperText="Brief description of this travel type"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select
                  value={formData.icon}
                  onChange={(e) =>
                    setFormData({ ...formData, icon: e.target.value })
                  }
                  label="Icon"
                >
                  {iconOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Color</InputLabel>
                <Select
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  label="Color"
                >
                  {colorOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Chip
                        label={option.label}
                        size="small"
                        color={option.value}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={formData.display_order}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    display_order: parseInt(e.target.value) || 0,
                  })
                }
                inputProps={{ min: 0 }}
                helperText="Order in which to display"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requires_advance}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        requires_advance: e.target.checked,
                      })
                    }
                  />
                }
                label="Requires Travel Advance"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.name || !formData.code}
          >
            {editingType ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TravelRequestTypeSettingsPage;
